/* Explanation screen for Tag Goals & Streaks.
   Covers creating goals, tracking progress, and maintaining streaks. */

import {
  Target,
  Flame,
  Tag,
  Plus,
  Clock,
  TrendingUp,
  CheckCircle2,
  BarChart3,
  Sparkles,
  Award,
} from "lucide-react";
import {
  FeatureInfoDialog,
  InfoSection,
  InfoSectionTitle,
  InfoStep,
  InfoHeroBadge,
} from "./FeatureInfoDialog";

interface GoalsInfoProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function GoalProgressMockup() {
  const goals = [
    { name: "Learning", tags: ["tech", "science"], target: 60, current: 42, color: "var(--color-chart-1)" },
    { name: "Fitness", tags: ["health"], target: 30, current: 30, color: "var(--color-chart-3)" },
    { name: "Entertainment", tags: ["fun", "gaming"], target: 45, current: 15, color: "var(--color-chart-2)" },
  ];

  return (
    <div className="rounded-xl border border-border/60 bg-card p-3.5 space-y-3 max-w-[280px] mx-auto">
      {/* Streak header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Flame className="size-4 text-chart-5" />
          <span className="text-xs font-bold">7-day streak</span>
        </div>
        <span className="text-[9px] text-muted-foreground">2 of 3 goals hit</span>
      </div>

      {/* Goal bars */}
      {goals.map((goal) => {
        const pct = Math.min(100, Math.round((goal.current / goal.target) * 100));
        const isDone = pct >= 100;
        return (
          <div key={goal.name} className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                {isDone ? (
                  <CheckCircle2 className="size-3" style={{ color: goal.color }} />
                ) : (
                  <Target className="size-3" style={{ color: goal.color }} />
                )}
                <span className="text-[10px] font-semibold">{goal.name}</span>
              </div>
              <span className="text-[9px] text-muted-foreground">
                {goal.current}m / {goal.target}m
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${pct}%`,
                  backgroundColor: goal.color,
                  opacity: isDone ? 1 : 0.7,
                }}
              />
            </div>
            <div className="flex gap-1">
              {goal.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-[7px] font-medium px-1 py-0 rounded-full bg-muted text-muted-foreground"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function GoalsInfo({ open, onOpenChange }: GoalsInfoProps) {
  return (
    <FeatureInfoDialog
      open={open}
      onOpenChange={onOpenChange}
      title="About Goals & Streaks"
    >
      <div className="space-y-5">
        {/* Hero */}
        <div className="text-center space-y-3">
          <InfoHeroBadge>
            <Flame className="size-3" />
            Stay Balanced
          </InfoHeroBadge>
          <h2 className="text-xl font-bold tracking-tight">
            Goals & Streaks
          </h2>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
            Set daily time targets for different types of content. Track
            progress in real time and build streaks by hitting your goals
            every day.
          </p>
        </div>

        {/* Visual preview */}
        <div className="rounded-xl border border-border/40 bg-muted/20 p-4">
          <GoalProgressMockup />
        </div>

        {/* Creating a goal */}
        <InfoSection>
          <InfoSectionTitle
            icon={<Plus className="size-4" />}
            iconColor="text-chart-1"
            iconBg="bg-chart-1/12"
          >
            Creating a Tag Goal
          </InfoSectionTitle>
          <div className="space-y-3">
            <InfoStep
              number={1}
              icon={<Target className="size-4" />}
              color="var(--color-chart-1)"
              title="Open the Goals section"
              description="Find it in the sidebar — your current goals and progress are always visible."
            />
            <InfoStep
              number={2}
              icon={<Plus className="size-4" />}
              color="var(--color-chart-2)"
              title="Click Add Goal"
              description="Give your goal a name like 'Learning' or 'Fitness.'"
            />
            <InfoStep
              number={3}
              icon={<Tag className="size-4" />}
              color="var(--color-chart-3)"
              title="Select tags"
              description="Pick which tags count toward this goal. A goal can include multiple tags (e.g., 'tech' and 'science')."
            />
            <InfoStep
              number={4}
              icon={<Clock className="size-4" />}
              color="var(--color-chart-4)"
              title="Set a daily target"
              description="Choose a daily time target in minutes. Start realistic — 30 min/day is a good starting point."
            />
          </div>
        </InfoSection>

        {/* How tracking works */}
        <InfoSection>
          <InfoSectionTitle
            icon={<BarChart3 className="size-4" />}
            iconColor="text-chart-3"
            iconBg="bg-chart-3/12"
          >
            How Goals Are Tracked
          </InfoSectionTitle>
          <p className="text-xs text-muted-foreground leading-relaxed mb-3">
            When you complete an item that has tags matching a goal:
          </p>
          <div className="space-y-2">
            {[
              {
                icon: <TrendingUp className="size-3.5" />,
                text: "The item's duration is added to your daily progress",
                color: "text-chart-1",
                bg: "bg-chart-1/10",
              },
              {
                icon: <BarChart3 className="size-3.5" />,
                text: "Your progress bar in the sidebar updates in real time",
                color: "text-chart-3",
                bg: "bg-chart-3/10",
              },
              {
                icon: <Flame className="size-3.5" />,
                text: "Meeting all goals for the day extends your streak",
                color: "text-chart-5",
                bg: "bg-chart-5/10",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-2.5 rounded-lg border border-border/40 bg-card px-3 py-2"
              >
                <div
                  className={`size-7 rounded-lg ${item.bg} flex items-center justify-center ${item.color} shrink-0`}
                >
                  {item.icon}
                </div>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </InfoSection>

        {/* Streaks */}
        <InfoSection>
          <InfoSectionTitle
            icon={<Flame className="size-4" />}
            iconColor="text-chart-5"
            iconBg="bg-chart-5/12"
          >
            Daily Streaks
          </InfoSectionTitle>
          <p className="text-xs text-muted-foreground leading-relaxed mb-3">
            FeedKiwi tracks a daily streak for completing your goals. The
            longer your streak, the bigger the rewards:
          </p>
          <div className="space-y-2">
            {[
              {
                icon: <Flame className="size-3" />,
                label: "Streak counter",
                desc: "Always visible in the sidebar header",
              },
              {
                icon: <CheckCircle2 className="size-3" />,
                label: "Flame icon",
                desc: "Shows when today's goals are all complete",
              },
              {
                icon: <Award className="size-3" />,
                label: "Kiwi rewards",
                desc: "Earn kiwi skins on reward days—daily week one, every 3 days, then weekly",
              },
            ].map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-2.5"
              >
                <div className="size-6 rounded-md bg-chart-5/10 flex items-center justify-center text-chart-5 shrink-0">
                  {item.icon}
                </div>
                <div>
                  <p className="text-[10px] font-semibold">{item.label}</p>
                  <p className="text-[9px] text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </InfoSection>

        {/* AI tip */}
        <div className="rounded-xl bg-ai-accent/5 border border-ai-accent/15 p-4">
          <div className="flex items-start gap-3">
            <div className="size-8 rounded-lg bg-ai-accent/12 flex items-center justify-center shrink-0">
              <Sparkles className="size-4 text-ai-accent" />
            </div>
            <div>
              <p className="text-xs font-semibold">Create Goals with AI</p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                Ask KiwiAI: "Create a goal for 60 minutes of learning content
                per day" and it will set one up for you instantly.
              </p>
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="rounded-xl bg-primary/5 border border-primary/15 p-4">
          <p className="text-xs font-semibold text-primary mb-2">Tips</p>
          <ul className="space-y-1.5 text-xs text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              Start realistic — set achievable goals and increase over time.
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              Be specific — create separate goals for learning, fitness, etc.
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              Use multiple tags — a "learning" goal can track both "tech" and "science."
            </li>
          </ul>
        </div>
      </div>
    </FeatureInfoDialog>
  );
}
